import { FindexClient } from "@/integrations/findex";
import Button from "components/button";
import { popModal, pushModal } from "components/modal/store";
import { pushToast } from "components/toast/hooks";
import { useSession } from "@/modules/auth/hooks/useSession";
import Icon from "components/icon";
import { CollapsedSection } from "components/collapsed-section";


export const pushDisclaimerModal = (): Promise<boolean> => {
  const session = useSession();
  let scrollableElement: HTMLElement | null = null;
  const scrolledToBottom = ref<boolean>(false)
  const notAtBottom = computed(() => !scrolledToBottom.value)

  const scrollToBottom = () => {
    const el = scrollableElement;
    if (!el) return;

    nextTick(() => {
      // Calculate the distance left to scroll
      const scrollDistance = el.scrollHeight - (el.scrollTop + el.clientHeight);

      el.scrollBy({
        top: scrollDistance,
        behavior: 'smooth'
      });
    });
  };

  const checkScroll = (el: HTMLElement) => {
    const clientHeight = Math.ceil(el.clientHeight);
    const scrollHeight = Math.ceil(el.scrollHeight);

    // Check 1: Content is fully visible (NO SCROLLBAR)
    const isContentFullyVisible = clientHeight >= scrollHeight;
    // Check 2: Scrolled to bottom (only if scrollbar exists)
    let isScrolledToBottom = false;
    if (!isContentFullyVisible) {
      const scrollTop = Math.ceil(el.scrollTop);
      const tolerance = 12;
      isScrolledToBottom = (scrollTop + clientHeight) >= (scrollHeight - tolerance);
    }

    scrolledToBottom.value = isContentFullyVisible || isScrolledToBottom;
  };

  return new Promise<boolean>((resolve) => {
    pushModal({
      title: "Midas AI Terms & Conditions",
      fullscreen: true,
      onClose: () => {
        resolve(false);
      },
      onContentReady: (el: HTMLElement) => {
        scrollableElement = el;
        checkScroll(el);
      },
      onScroll: (el: HTMLElement) => {
        scrollableElement = el
        checkScroll(el);
      },
      actions: () => {
        const loading = ref<boolean>(false);
        const handleAgree = async () => {
          loading.value = true;
          try {
            await FindexClient.agreeAIAssistantTerms();
            pushToast({
              variant: "success",
              title: "Accepted",
              subtitle: "You can now use Midas AI",
              duration: 5.0
            });
            popModal();
            session.patchLocalUser((user) => {
              return {
                ...user,
                agreedAIUsageDate: new Date()
              };
            });
            resolve(true);
          } catch (e) {
            console.error(e);
            pushToast({
              variant: "error",
              title: "There was an error",
              subtitle: "Please try again later",
              duration: 5.0
            });
          }
          loading.value = false;
        };

        const handleCancel = () => {
          popModal();
          resolve(false);
        };

        return (
          <div class="grid grid-cols-[max-content_max-content] gap-2 justify-end">
            <Button onClick={handleCancel} variant="transparent" outlined>
              Cancel
            </Button>
            <Button onClick={handleAgree} isLoading={loading} disabled={notAtBottom.value}>
              Activate Midas AI
            </Button>
          </div>
        );
      },
      body: () => {
        return (
          <div class="w-full h-full grid justify-center items-center relative">
            <div class="w-full lg:max-w-[788px] sm:p-8 sm:outline-solid sm:outline-1 sm:-outline-offset-1 sm:outline-gray-300 inline-flex flex-col justify-center items-center gap-6">
              <div
                data-dark-mode="False"
                data-show-icon="true"
                data-show-right-content="false"
                data-type="Stats + Changes"
                class="self-stretch inline-flex justify-start items-center "
              >
                <div class="flex-1 inline-flex flex-col justify-center items-center gap-6">
                  {notAtBottom.value && (
                    <div class="fixed bottom-24 right-[50%] z-10 ">
                      <Button
                        class="rounded-full bg-primary-500 border-2 border-white"
                        onlyIcon
                        shadowHover
                        onClick={scrollToBottom}>
                        <Icon
                          src={async () => import('assets/img/svg/icons/chevron.svg?raw')}
                          fill="white" size="20px" class="transform rotate-270" />
                      </Button>
                    </div>
                  )}
                  <div class="flex flex-col gap-2">
                    <div class="justify-start text-gray-900 text-sm lg:text-lg font-semibold font-['Inter'] ">
                      What is Midas AI (Beta)?
                    </div>
                    <div class="self-stretch justify-start text-gray-500 text-xs lg:text-sm font-normal font-['Inter'] leading-normal">Midas is your AI-powered financial co-pilot that helps you understand, manage, and optimize your complete investment portfolio. Ask questions in natural language and get institutional-grade insights that were previously only available to ultra-high-net-worth individuals.
                    </div>
                  </div>

                  <div class="flex gap-6 lg:gap-3 flex-wrap-reverse lg:flex-nowrap justify-center items-center">
                    <div class="flex flex-col gap-2 sm:gap-3 w-full sm:max-w-[530px] lg:max-w-[267px]">
                      <div class="border flex flex-col gap-[2px] border-[#3F83F8] bg-primary-100 py-[10px] px-3 rounded-sm">
                        <span class="flex gap-1 text-[#3F83F8] text-sm items-center justify-start font-semibold">
                          <Icon
                            size="14px"
                            stroke="#3F83F8"
                            src={async () =>
                              import("assets/img/svg/icons/ai-avatar.svg?raw")
                            } />
                          Smart Actions & Administration
                        </span>
                        <div class="text-gray-700 text-xs font-normal leading-normal align-self-start">Let Midas handle portfolio management tasks for you. Add investments, update valuations, organize documents, and manage your financial data through simple conversational requests.</div>
                      </div>
                      <div class=" border flex flex-col gap-[2px] border-[#3F83F8] bg-primary-100 py-[10px] px-3 rounded-sm">
                        <span class="flex gap-1 text-[#3F83F8] text-sm items-center justify-start font-semibold">
                          <Icon
                            size="14px"
                            stroke="#3F83F8"
                            src={async () =>
                              import("assets/img/svg/icons/ai-avatar.svg?raw")
                            } />
                          Portfolio Intelligence & Analytics                        </span>
                        <div class="text-gray-700 text-xs font-normal leading-normal">Ask questions about your investments and receive AI-powered insights instantly. Midas analyzes your portfolio to help you understand risk, diversification, performance, and more.</div>
                      </div>
                      <div class=" border flex flex-col gap-[2px] border-[#3F83F8] bg-primary-100 py-[10px] px-3 rounded-sm">
                        <span class="flex gap-1 text-[#3F83F8] text-sm items-center justify-start font-semibold">
                          <Icon
                            size="14px"
                            stroke="#3F83F8"
                            src={async () =>
                              import("assets/img/svg/icons/ai-avatar.svg?raw")
                            } />
                          Insights & Optimizations
                        </span>
                        <div class="text-gray-700 text-xs font-normal leading-normal">Identify patterns and opportunities in your portfolio data. Midas highlights information like potential rebalancing needs, tax-related data, and portfolio characteristics to help you conduct your own analysis.</div>
                      </div>
                    </div>
                    <div class="w-full min-w-[308px] min-h-[300px] sm:max-w-[528px] lg:min-w-[448px]">
                      <Icon
                        src={async () => import("assets/img/svg/icons/midas-AI.svg?raw")}
                        class="flex w-full h-full" />
                    </div>
                  </div>
                  <div class="flex flex-col gap-2">
                    <div class="justify-start text-gray-900 text-base lg:text-lg font-semibold font-['Inter'] leading-[18px] self-stretch pt-6">
                      Important Notice - Please read carefully before using Findex
                      AI
                    </div>
                    <div class="self-stretch justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                      By opting in to use the Findex AI Agent ("AI Agent"), you
                      acknowledge that you have read, understood, and agree to the
                      following terms and conditions:
                    </div>
                  </div>
                  <div class="flex self-start">
                    <CollapsedSection title="Terms & Conditions for Midas AI" class="text-xs sm:text-base" >
                      <div class="w-full flex flex-col gap-6 p-4 border border-gray-300 rounded-sm">
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            1. Your Financial Co-Pilot - Not Financial Advice
                          </div>
                          <div class="self-stretch justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                            <p>The Findex AI Agent is designed to serve as your <span class="font-bold">financial co-pilot</span>, providing you with data-driven insights, analysis tools, and portfolio information to help you conduct your own financial analysis. <span class="font-bold">The AI Agent empowers you to analyze your investments yourself</span> by organizing your data, highlighting trends, and presenting relevant information in an accessible format.</p>
                            <p class="mt-4"><span class="font-bold">This information is for educational and analytical purposes only and does not constitute financial, investment, tax, legal, or any type of professional advice</span>. The AI Agent can help you understand your portfolio better, but all investment decisions remain entirely your own. You should consult with qualified financial advisors, tax professionals, or other appropriate professionals before making any significant investment decisions.</p>
                          </div>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            2. AI Limitations and Accuracy
                          </div>
                          <ul class="list-disc text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>The AI Agent uses algorithms and machine learning to analyze data and provide insights</li>
                            <li><span class="font-bold">No guarantee of accuracy:</span> While we strive for accuracy, the AI Agent may produce errors, incomplete information, or outdated analysis</li>
                            <li><span class="font-bold">Market volatility:</span> Financial markets are unpredictable, and past performance does not guarantee future results</li>
                            <li><span class="font-bold">Data dependency:</span> The AI Agent's effectiveness depends on the quality and completeness of your input data</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            3. AI Limitations and Accuracy
                          </div>
                          <p class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">You are solely responsible for:</p>
                          <ul class="list-disc mt-4 text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>Verifying all information and recommendations provided by the AI Agent.</li>
                            <li>Making your own investment decisions.</li>
                            <li>Understanding the risks associated with your investments.</li>
                            <li>Complying with applicable laws and regulations in your jurisdiction.</li>
                            <li>Maintaining the security of your account and data.</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            4. No Fiduciary Relationship
                          </div>
                          <p class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                            Findex and the AI Agent do not act as your fiduciary, investment advisor, or financial planner. <span class="font-bold">However, the AI Agent is designed to always aim to have your best interests in mind</span> by providing objective, data-driven analysis without bias toward any particular investment products or services.</p>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            5. Data Processing and Privacy
                          </div>
                          <ul class="list-disc text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>The AI Agent processes your financial data to provide personalized insights.</li>
                            <li>Your data is handled according to our&nbsp;
                              <a onClick={e => e.stopPropagation()} target="_blank" rel="noopener noreferrer" href="https://www.findex.se/privacy-policy" class="text-primary-500 hover:text-primary-700 hover:underline"> Privacy Policy.
                              </a>
                            </li>
                            <li>We use secure and best-in-class third-party providers (Auth0, Amazon Web Services, and BankID) but can never guarantee absolute security.</li>
                            <li><span class="font-bold">Data encryption: All data is encrypted.</span></li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            6. Technical Limitations
                          </div>
                          <ul class="list-disc text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>The AI Agent may experience downtime, technical issues, or service interruptions.</li>
                            <li>We do not guarantee continuous availability or error-free operation.</li>
                            <li>System updates may temporarily affect AI Agent functionality.</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            7. Third-Party Data and Integrations
                          </div>
                          <ul class="list-disc text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>The AI Agent may rely on third-party data sources and bank integrations.</li>
                            <li>We are not responsible for the accuracy or availability of third-party data.</li>
                            <li>Integration delays or failures may affect AI Agent performance.</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            8. Geographic and Regulatory Limitations
                          </div>
                          <ul class="list-disc text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>The AI Agent is designed for users in jurisdictions where Findex operates.</li>
                            <li>You are responsible for ensuring compliance with local financial regulations.</li>
                            <li>Some features may not be available in all jurisdictions.</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            9. Limitation of Liability
                          </div>
                          <p class="text-gray-500 text-sm font-bold font-['Inter'] leading-[21px]">TO THE MAXIMUM EXTENT PERMITTED BY LAW</p>
                          <ul class="list-disc mt-4 text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>Findex shall not be liable for any direct, indirect, incidental, consequential, or punitive damages.</li>
                            <li>This includes financial losses resulting from AI Agent recommendations or system failures.</li>
                            <li>Our total liability shall not exceed the fees paid by you for the AI Agent service in the 12 months preceding the claim.</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            10. Beta/Early Access Features
                          </div>
                          <p class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                            If you are using early access or beta features:</p>
                          <ul class="list-disc mt-4 text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-4">
                            <li>These features are experimental and may contain bugs or errors.</li>
                            <li>Functionality may change without notice.</li>
                            <li>Additional risks may apply to experimental features.</li>
                          </ul>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            11. Modification of Terms
                          </div>
                          <p class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                            We reserve the right to modify this disclaimer at any time. Continued use of the AI Agent after modifications constitutes acceptance of the updated terms.</p>
                        </div>
                        <div class="flex flex-col gap-2">
                          <div class="justify-start text-gray-900 text-[16px] font-bold font-['Inter'] leading-[24px] self-stretch ">
                            12. Governing Law
                          </div>
                          <p class="text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                            This disclaimer is governed by Swedish law, and any disputes shall be resolved in Swedish courts.</p>
                          <div class="text-gray-500 text-xs font-['Inter'] leading-[18px] mt-4">
                            <p>For questions about this disclaimer contact team@findex.se</p>
                            <p>Last updated: 12 Sep, 2025</p>
                          </div>
                        </div>
                      </div>
                    </CollapsedSection>
                  </div>
                  <div class="w-full flex flex-col gap-2 items-center p-4 border border-gray-300 rounded-sm bg-gray-100 mb-4 sm:mb-0">
                    <div class="justify-start text-gray-900 text-base lg:text-lg font-semibold font-['Inter'] leading-[18px] self-stretch">
                      Acknowledgement & Consent
                    </div>
                    <div class="self-stretch justify-start text-gray-500 text-sm font-normal font-['Inter'] leading-[21px]">
                      <p class="mb-4">By clicking "Activate Midas AI" or continuing to use the Findex AI Agent, you acknowledge that:</p>

                      <ul class=" text-gray-500 text-sm font-normal font-['Inter'] leading-[21px] pl-6 mb-4 list-disc">
                        <li>You have read and understood the <span class="font-bold">Terms & Conditions for Midas AI.</span></li>
                        <li>You understand that the AI Agent does not provide financial advice.</li>
                        <li>You will not rely solely on AI Agent recommendations for investment decisions.</li>
                        <li>You understand the risks associated with using AI-powered financial tools.</li>
                        <li>You agree to use the AI Agent at your own risk.</li>
                        <li>You will consult with qualified professionals for financial advice.</li>
                      </ul>

                      <p>If you do not agree with these terms, please do not use the Findex AI Agent.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div >
        );
      },
    })


  });
};
